import {Modal, Form, Col, Row, Button, Input, Spin, message} from "antd";
import React, {useState, useEffect, useCallback, useRef} from "react";
import api, {getApiErrorData} from "../../api";
import {calculateCropDates, calculateRowsAndPlants} from "../../helpers";
import {GENERIC_ERROR_MESSAGE} from "../../constants";
import {useNavigate} from "react-router-dom";
import CropSearchResults from "../CropSearchResults";
import {useTranslations} from "../../lang";

export default function PlantingModal({open, bed, companyId, onClose, onPlantingAdded}) {

    const {translate} = useTranslations()
    const [searchResults, setSearchResults] = useState({});
    const [form] = Form.useForm();
    const searchInputRef = useRef(null);
    const [searchingForCrops, setSearchingForCrops] = useState(false);
    const [step, setStep] = useState(null);
    const [addingCrop, setAddingCrop] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.crops({}).then((crops) => {
            setSearchResults({
                "user": crops
            })
        });
    }, [open]);

    const onCancel = useCallback(() => {
        setSearchingForCrops(false)
        setAddingCrop(false)
        setStep(null)
        form.resetFields()
        onClose()
    }, [form, onClose])

    const save = useCallback((values, crop) => {
        api.addPlanting(values).then((planting) => {
            onPlantingAdded(planting)
            onCancel()
        }).catch((error) => {
            if (error.response && error.response.status === 422) {
                Modal.error({
                    title: translate('Er is iets misgegaan'),
                    content: (
                        <div>
                            <p>{translate('Voor dit gewas is de informatie incompleet, vul eerst alle gegevens in van het gewas. Als dit gedaan is kunnen we hem in de planner zetten.')}</p>
                            {crop && (
                                <Button onClick={() => navigate(crop.is_fully_configured ? `/crops/${crop.id}` : `/crops?cropId=${crop.id}`)}>
                                    {translate('Gewas bewerken')}
                                </Button>
                            )}
                        </div>
                    )
                })
            } else {
                message.info(GENERIC_ERROR_MESSAGE)
            }
        })
    }, [onPlantingAdded, onCancel, translate, navigate])

    function handleSearch() {
        setSearchingForCrops(true)
        setStep(translate('Gewassen zoeken...'))
        api.search(form.getFieldValue('query')).then(setSearchResults).finally(() => {
            setSearchingForCrops(false)
            setStep(null)
        })
    }

    const addCropToBed = useCallback((crop) => {
        const cropDates = calculateCropDates(crop, null)
        setStep(translate('We voegen het gewas toe...'))

        if (bed) {
            // If we have a bed, calculate rows and plants
            const rowsAndPlants = calculateRowsAndPlants(bed, crop)
            save({bed_id: bed.id, crop_id: crop.id, ...rowsAndPlants, ...cropDates}, crop)
        } else if (companyId) {
            // No bed - create a bedless planting linked to company (cropplan)
            save({company_id: companyId, crop_id: crop.id, ...cropDates}, crop)
        }
    }, [bed, companyId, translate, save])

    const showCropLimitError = useCallback(() => {
        Modal.error({
            title: translate('Je hebt het maximum aantal gewassen bereikt'),
            okText: translate('Ga naar account instellingen'),
            onOk: () => navigate('/account'),
            content: (
                <div>
                    <p>{translate('Je hebt het maximum aantal gewassen bereikt voor je account.')}</p>
                    <p>{translate('Wil je meer gewassen toevoegen? Upgrade dan.')}</p>
                </div>
            )
        })
    }, [translate, navigate])

    const planCrop = useCallback((crop) => {
        // User crops always have an id, so we can add them directly to the bed
        setAddingCrop(true)
        addCropToBed(crop)
    }, [addCropToBed])

    const handleCropFound = useCallback((crop) => {
        // For product_database crops, we can use them directly
        setAddingCrop(true)
        setStep(translate('Gewas toevoegen aan jouw account...'))

        api.createCrop(crop).then(addCropToBed).catch((error) => {
            if (getApiErrorData(error).error === "CropLimitExceededException") {
                showCropLimitError()
                return
            }

            message.info(GENERIC_ERROR_MESSAGE)
        }).finally(() => {
            setStep(null)
            setAddingCrop(false)
        })
    }, [addCropToBed, translate, showCropLimitError])

    return (
        <Modal open={open} footer={null} onCancel={onCancel} afterOpenChange={(isOpen) => { if (isOpen) searchInputRef.current?.focus() }}>
            <h1>{translate('Nieuw gewas plannen')}</h1>
            <Form
                layout="vertical"
                form={form}
            >
                <Row gutter={[18, 18]}>
                    <Col span={18}>
                        <Form.Item label={translate('Zoek naar (nieuwe) gewassen')} name="query">
                            <Input ref={searchInputRef} style={{width: '100%'}} onPressEnter={handleSearch} disabled={searchingForCrops}/>
                        </Form.Item>
                    </Col>
                    <Col span={6} className={"text-right"}>
                        <Form.Item label="&nbsp;">
                            <Button onClick={() => handleSearch()} type={"primary"}>{translate('Zoeken')}</Button>
                        </Form.Item>
                    </Col>
                </Row>

                {(searchingForCrops || addingCrop) ? (
                    <div className={"text-center"}>
                        <Spin/>
                        <br/>{step}
                    </div>
                ) : null}

                {(!addingCrop && !searchingForCrops) && searchResults && (
                    <CropSearchResults
                        showOwnCrops={true}
                        searchResults={searchResults}
                        onSelect={planCrop}
                        onCropFound={handleCropFound}
                    />
                )}
            </Form>
        </Modal>
    )
}